import type { CanonicalSubsegment, SpecialtyToken } from '../taxonomy.js';

// ── Clinical-specialism vocabulary ───────────────────────────────────────────
// The blueprint "X-factor" axis: WITHIN a subsegment, what a target actually
// specialises in (dementia care, implant dentistry, out-of-hours emergency vet).
//
// This is DISTINCT from the calibration-locked revenue `SPECIALTIES` axis in
// taxonomy.ts. That axis is one-per-operator and routes revenue formulas
// (model_subsegment, footprint in enm.specialties_present — OWNED + rewritten by
// the market agents). This one is MULTI-TAG, buyer/thesis-driven, and lives in
// its OWN column enm.clinical_specialisms[] so the agents' revenue-footprint
// writes never clobber it — a care home can be dementia + palliative + complex_care
// at once. Nursing is a legacy service-type compatibility value, not an active
// web specialism, and is never inferred for a new home. It never enters revenue
// routing. specialism_fit reads the UNION of the
// two columns.
//
// Detection follows the layered-resolution passes (see BUILD_PLAN.md):
//   statutory  → CQC `specialisms`, FINESS disciplines
//   name       → trading-name / NACE heuristics (isGb*ByName family)
//   web        → free website/press extraction (services/web-evidence)
// Per-(market × specialism) detection state lives in blueprint_market_capability.

/** Where we expect a specialism to be detectable — drives the honesty layer. */
export type SpecialismSource = 'statutory' | 'name' | 'web';

/** Phase-1 launch priority vs later per-market expansion. */
export type SpecialismPriority = 'launch' | 'later';

export interface ClinicalSpecialism {
  /** Stable machine token (snake_case). Frozen once shipped. */
  token: string;
  /** Display label. */
  label: string;
  /** Canonical subsegment(s) this specialism refines. Multi-parent where the
   *  same clinical focus spans settings (e.g. dementia across residential +
   *  domiciliary). */
  parents: CanonicalSubsegment[];
  priority: SpecialismPriority;
  /** Historical compatibility only; never infer this token for new homes. */
  legacyOnly?: boolean;
  /** Expected detection passes, best-first. */
  detect: SpecialismSource[];
  /** Cross-reference to the calibration-locked REVENUE axis (taxonomy.ts
   *  SPECIALTIES). Set where this clinical tag coincides with an economically
   *  distinct sub-subsegment: there the revenue calibration is AUTHORITATIVE —
   *  specialism_fit reads model_subsegment, and the blueprint layer never
   *  re-models the economics. Unset ⇒ a within-subsegment positioning tag with
   *  no distinct revenue formula. A newly-discovered distinct specialism is
   *  REFERRED to the calibration agent (handover/subsegments/), not added here. */
  revenueSpecialty?: SpecialtyToken;
  /** Local-language synonyms for web/statutory normalisation, keyed by ISO 639-1
   *  LANGUAGE code (not market) so a term is authored once and reused across
   *  every market that files in it — resolved via MARKET_LANGUAGES in markets.ts.
   *  Seeded for launch tokens; filled per language at ingest. */
  synonyms?: Record<string, string[]>;
}

export const CLINICAL_SPECIALISMS = [
  // ── Care: residential + specialist + domiciliary ──────────────────────────
  {
    token: 'dementia',
    label: 'Dementia / memory care',
    parents: ['Residential elderly care', 'Residential specialist care', 'Domiciliary / home care'],
    priority: 'launch',
    detect: ['statutory', 'name', 'web'],
    synonyms: {
      de: ['Demenz', 'Alzheimer', 'gerontopsychiatrisch'],
      fr: ['Alzheimer', 'démence', 'unité protégée'],
      nl: ['dementie', 'psychogeriatrie'],
      es: ['demencia', 'Alzheimer'],
      it: ['demenza', 'Alzheimer'],
      da: ['demens'],
      sv: ['demens'],
      nb: ['demens'],
      pl: ['demencj', 'Alzheimer'],
      cs: ['demence', 'Alzheimer'],
      pt: ['demência', 'Alzheimer'],
      fi: [
        'muistisairaus',
        'muistisairaat',
        'muistisairaan',
        'muistisairaiden',
        'muistisairaille',
        'muistikoti',
        'muistikuntoutus',
        'muistikuntoutusryhmä',
        'muistihäiriö',
        'dementia',
      ],
      ro: ['demență', 'Alzheimer'],
      hu: ['demencia'],
      el: ['άνοια', 'Αλτσχάιμερ'],
    },
  },
  {
    token: 'nursing',
    label: 'Nursing care (with nursing)',
    parents: ['Residential elderly care', 'Residential specialist care'],
    priority: 'launch',
    legacyOnly: true,
    detect: ['statutory', 'name', 'web'],
    revenueSpecialty: 'nursing_home',
    synonyms: {
      de: ['Pflegeheim', 'vollstationäre Pflege'],
      // 'soins' alone is any care at all — a nursing tag needs the
      // medicalised/infirmier qualifier or the EHPAD legal form.
      fr: ['EHPAD', 'médicalisé', 'soins infirmiers'],
      nl: ['verpleeghuis'],
      es: ['residencia asistida'],
      it: ['RSA', 'residenza sanitaria'],
      da: ['plejehjem'],
      sv: ['sjukhem'],
      nb: ['sykehjem'],
      cs: ['ošetřovatelsk'],
      pl: ['zakład opiekuńczo-leczniczy'],
    },
  },
  // ── A NOTE ON `parents` FOR THE CARE TOKENS (2026-09-20) ──────────────────
  // These tokens split into two kinds, and conflating them is what put three of
  // them under the wrong parents:
  //   POPULATION — the residents are a distinct group, which DEFINES the
  //     facility: learning_disability, mental_health, autism,
  //     acquired_brain_injury, substance_misuse. On a home whose sub-segment
  //     says elderly care, one of these usually means the ROW IS MIS-TYPED, so
  //     the narrow parents are kept and they still gate inference.
  //   CAPABILITY — a clinical capability an elderly home holds ALONGSIDE older
  //     people: dementia, nursing, palliative, complex_care,
  //     physical_sensory_disability. `dementia` and `nursing` already carried
  //     'Residential elderly care'; the other three did not, and were wrong.
  //
  // MEASURED, not argued. CQC is the only regulator that publishes the cohort
  // set AND the age band, so it can settle it: of 3,696 English care homes
  // registered for OVER-65s ONLY, 73% carry dementia, 34% physical/sensory
  // disability, 15% mental health, 5% learning disability, 0.4% substance
  // misuse. "Specialist only" is simply false for the top of that list.
  // Corroborating: `entity_normalised_metrics.clinical_specialisms` already
  // holds `palliative` on 264 elderly-care entities against 44 specialist ones,
  // and NAME_RULES in classify.ts has always used CARE_PARENTS (all three care
  // sub-segments) for these tokens — so the name pass wrote them to elderly
  // homes while this list said it could not happen. This aligns the two.
  //
  // Nothing economic moves: `palliative`, `complex_care` and
  // `physical_sensory_disability` do not exist on the calibration-locked
  // SPECIALTIES axis, so isSpecialtyUnderParent and revenue routing are
  // untouched. `parents` here gates only the synonym pass and the group cascade.
  {
    token: 'complex_care',
    label: 'Complex / continuing care',
    // Continuing care is acuity, not a client group: a Polish ZOL licensed in
    // `Anestezjologia i intensywna terapia` with ventilated beds is an elderly
    // long-term nursing institution.
    parents: ['Residential elderly care', 'Residential specialist care', 'Domiciliary / home care'],
    priority: 'launch',
    detect: ['statutory', 'web'],
    synonyms: {
      fi: [
        'vaativa hoito',
        'vaativaa hoitoa',
        'erityistä huolenpitoa',
        'erityistä hoivaa',
        'erityistä huolenpitoa ja hoivaa',
        'hoidolliset asukkaat',
        'hoidollinen',
        'monisairaat',
        'monisairaille',
        'paljon hoivaa',
        'paljon hoitoa',
        'hengityskone',
      ],
    },
  },
  {
    token: 'learning_disability',
    label: 'Learning disability',
    parents: ['Residential specialist care', 'Domiciliary / home care'],
    priority: 'launch',
    detect: ['statutory', 'web'],
    synonyms: {
      fi: ['kehitysvamma', 'kehitysvammaiset', 'kehitysvammaisille', 'kehitysvammaisten'],
    },
  },
  {
    token: 'mental_health',
    label: 'Mental health',
    parents: ['Residential specialist care', 'Specialist medical'],
    priority: 'launch',
    detect: ['statutory', 'web'],
    synonyms: {
      fi: [
        'mielenterveys',
        'mielenterveyskuntoutuja',
        'mielenterveyskuntoutujat',
        'mielenterveyskuntoutujille',
        'psykiatrinen',
        'psykiatriset',
      ],
    },
  },
  {
    token: 'palliative',
    label: 'Palliative / end-of-life',
    // An elderly home is where most European care-home deaths happen, and five
    // regulators record it on one: a Norwegian *lindrende enhet* is a ward INSIDE
    // a sykehjem, and Poland's RPWDL carries `Dziedzina medyczna = Medycyna
    // paliatywna` on a ZOL/ZPO's own cell. A standalone hospice is a different
    // thing — that is a mis-typed sub-segment, not a reason to refuse the token.
    parents: ['Residential elderly care', 'Residential specialist care', 'Domiciliary / home care'],
    priority: 'launch',
    detect: ['statutory', 'name', 'web'],
    // palliat/paliat + hospice stems live in the name regex; these are the
    // language-specific end-of-life terms the stem misses.
    synonyms: {
      fr: ['soins palliatifs', 'fin de vie'],
      de: ['Sterbebegleitung'],
      nl: ['terminale zorg'],
      fi: [
        'palliatiivinen',
        'palliatiivista',
        'palliatiiviseen',
        'saattohoito',
        'saattohoidon',
        'saattohoitoa',
        'saattohoitoon',
        'saattohoitokoti',
      ],
    },
  },
  {
    token: 'physical_sensory_disability',
    label: 'Physical / sensory disability',
    // 34% of CQC homes registered for over-65s ONLY carry it (1,258 homes).
    // Frailty, immobility and sensory loss are intrinsic to elderly care, so
    // excluding the elderly parent made a third of our best market unfilterable.
    parents: ['Residential elderly care', 'Residential specialist care', 'Domiciliary / home care'],
    priority: 'later',
    detect: ['statutory', 'web'],
    synonyms: {
      fi: [
        'liikuntavamma',
        'liikuntavammaiset',
        'vammautuneet',
        'vammautuneille',
        'monivammaisuus',
        'monivammaiset',
        'sotainvalidit',
        'näkövamma',
        'kuulovamma',
        'aistivamma',
      ],
    },
  },
  {
    token: 'acquired_brain_injury',
    label: 'Acquired brain injury / neuro-rehab',
    parents: ['Residential specialist care'],
    priority: 'later',
    detect: ['statutory', 'web'],
    synonyms: {
      fi: [
        'aivovamma',
        'aivovammat',
        'aivovammapotilaat',
        'neurologinen kuntoutus',
        'aivoverenkiertohäiriö',
        'aivohalvauskuntoutus',
      ],
    },
  },
  {
    token: 'substance_misuse',
    label: 'Substance misuse / rehab',
    parents: ['Residential specialist care'],
    priority: 'later',
    detect: ['statutory', 'web'],
    synonyms: {
      fi: [
        'päihdekuntoutus',
        'päihdekuntoutujat',
        'päihdeongelma',
        'päihdeongelmaiset',
        'päihdepalvelut',
        'riippuvuus',
        'alkoholiriippuvuus',
      ],
    },
  },
  {
    token: 'autism',
    label: 'Autism',
    parents: ['Residential specialist care', 'Domiciliary / home care'],
    priority: 'later',
    detect: ['statutory', 'web'],
    synonyms: { fi: ['autismi', 'autismikirjo', 'autistinen', 'autistiset'] },
  },

  // ── Dental ────────────────────────────────────────────────────────────────
  {
    token: 'orthodontics',
    label: 'Orthodontics',
    parents: ['Dental'],
    priority: 'launch',
    detect: ['name', 'web'],
  },
  {
    token: 'implantology',
    label: 'Implantology',
    parents: ['Dental'],
    priority: 'launch',
    detect: ['name', 'web'],
  },
  {
    token: 'cosmetic_dentistry',
    label: 'Cosmetic dentistry',
    parents: ['Dental'],
    priority: 'launch',
    detect: ['name', 'web'],
    synonyms: {
      de: ['ästhetische Zahn'],
      es: ['estética dental'],
      it: ['estetica dentale', 'odontoiatria estetica'],
      pl: ['stomatologia estetyczna'],
      fr: ['dentisterie esthétique'],
      pt: ['estética dentária'],
    },
  },
  {
    token: 'oral_maxillofacial_surgery',
    label: 'Oral & maxillofacial surgery',
    parents: ['Dental'],
    priority: 'later',
    detect: ['name', 'web'],
  },
  {
    token: 'endodontics',
    label: 'Endodontics',
    parents: ['Dental'],
    priority: 'later',
    detect: ['name', 'web'],
  },
  {
    token: 'paediatric_dentistry',
    label: 'Paediatric dentistry',
    parents: ['Dental'],
    priority: 'later',
    detect: ['name', 'web'],
  },

  // ── Ophthalmology / optical (clinical) ────────────────────────────────────
  {
    // `cataract` matches the display TAG the market agents already emit on
    // ophthalmology operators (Market-Agent Brief §1) — not a new token.
    token: 'cataract',
    label: 'Cataract surgery',
    parents: ['Optical', 'Specialist medical'],
    priority: 'launch',
    detect: ['name', 'web'],
    revenueSpecialty: 'ophthalmology',
    // cataract/catarat/katarakt stems live in the name regex; the Nordic
    // vernacular ("grey star") shares no stem.
    synonyms: {
      sv: ['grå starr', 'gråstarr'],
      da: ['grå stær'],
      nb: ['grå stær'],
    },
  },
  {
    token: 'refractive_laser',
    label: 'Refractive / laser eye surgery',
    parents: ['Optical', 'Specialist medical'],
    priority: 'later',
    detect: ['name', 'web'],
    revenueSpecialty: 'ophthalmology',
  },

  // ── Specialist medical / aesthetics ───────────────────────────────────────
  {
    token: 'aesthetics_cosmetic',
    label: 'Aesthetics / cosmetic (non-surgical & surgical)',
    parents: ['Specialist medical'],
    priority: 'launch',
    detect: ['name', 'web'],
  },
  {
    token: 'dermatology',
    label: 'Dermatology',
    parents: ['Specialist medical'],
    priority: 'launch',
    detect: ['name', 'web'],
    // dermatolog- is language-invariant (name regex); these are the
    // "skin doctor" vernaculars.
    synonyms: {
      de: ['Hautarzt', 'Hautklinik', 'Hautzentrum'],
      nl: ['huidkliniek', 'huidcentrum'],
      sv: ['hudläkare', 'hudklinik'],
      da: ['hudlæge', 'hudklinik'],
      nb: ['hudlege', 'hudklinikk'],
      fi: ['ihotautilääkäri'],
    },
  },
  {
    token: 'msk_orthopaedic_sports',
    label: 'MSK / orthopaedic / sports medicine',
    parents: ['Specialist medical', 'Physiotherapy & allied health'],
    priority: 'launch',
    detect: ['name', 'web'],
  },
  {
    token: 'fertility_ivf',
    label: 'Fertility / IVF',
    parents: ['Specialist medical'],
    priority: 'launch',
    detect: ['name', 'web'],
    revenueSpecialty: 'fertility_ivf',
    // fertilit-/ivf/Kinderwunsch live in the name regex. PMA is an acronym →
    // market-scoped here.
    synonyms: {
      fr: ['PMA', 'procréation'],
      es: ['reproducción asistida'],
      it: ['procreazione assistita', 'fecondazione'],
      cs: ['reprodukční medicín'],
      pl: ['in vitro'],
      el: ['γονιμότητα', 'εξωσωματική'],
    },
  },
  {
    token: 'pain_management',
    label: 'Pain management',
    parents: ['Specialist medical'],
    priority: 'later',
    detect: ['name', 'web'],
  },
  {
    token: 'bariatric',
    label: 'Bariatric',
    parents: ['Specialist medical'],
    priority: 'later',
    detect: ['name', 'web'],
  },
  {
    token: 'gastro_endoscopy',
    label: 'Gastroenterology / endoscopy',
    parents: ['Specialist medical', 'Diagnostics & labs'],
    priority: 'later',
    detect: ['name', 'web'],
  },

  // ── Veterinary (emergency vs non is a real buyer distinction) ──────────────
  {
    token: 'vet_emergency_ooh',
    label: 'Emergency / out-of-hours veterinary',
    parents: ['Veterinary'],
    priority: 'launch',
    detect: ['name', 'web'],
    synonyms: {
      en: ['24 hour', '24/7', 'out of hours', 'emergency vet'],
      de: ['Tiernotdienst', 'Notfall'],
      fr: ['urgences vétérinaires'],
    },
  },
  {
    token: 'vet_referral_specialist',
    label: 'Referral / specialist veterinary hospital',
    parents: ['Veterinary'],
    priority: 'launch',
    detect: ['name', 'web'],
    // Tierklinik/dierenziekenhuis live in the name regex; CHV is the French
    // regulated referral tier (acronym → market-scoped).
    synonyms: {
      fr: ['CHV', 'centre hospitalier vétérinaire'],
      sv: ['djursjukhus'],
      da: ['dyrehospital'],
      nb: ['dyresykehus'],
    },
  },
  {
    token: 'vet_equine',
    label: 'Equine veterinary',
    parents: ['Veterinary'],
    priority: 'launch',
    detect: ['name', 'web'],
  },
  {
    token: 'vet_farm_production',
    label: 'Farm / production-animal veterinary',
    parents: ['Veterinary'],
    priority: 'later',
    detect: ['name', 'web'],
  },
  {
    token: 'vet_exotics',
    label: 'Exotics veterinary',
    parents: ['Veterinary'],
    priority: 'later',
    detect: ['name', 'web'],
  },

  // ── Pharmacy (service model) ──────────────────────────────────────────────
  {
    token: 'clinical_homecare',
    label: 'Clinical homecare',
    parents: ['Pharmacy'],
    priority: 'later',
    detect: ['name', 'web'],
  },
  {
    token: 'distance_selling_online',
    label: 'Distance-selling / online pharmacy',
    parents: ['Pharmacy'],
    priority: 'later',
    detect: ['name', 'web'],
  },
  // ── Clinical disciplines (added 2026-08-02) ──────────────────────────────
  // Emitted by the commission-G web-research pass, which reads what a clinic
  // says it TREATS and so surfaces finer disciplines than the launch set. They
  // were already loading into enm.clinical_specialisms (the loader does not
  // gate on this vocabulary) but were INVISIBLE in the UI, because the Screener
  // filter and the column label map are both built from this array — 3,261
  // entities carried only these tokens and could not be filtered for at all.
  // Registering them makes existing data reachable; it adds no new rows.
  // No revenueSpecialty: these are within-subsegment positioning tags. A
  // discipline that turns out to be economically distinct is REFERRED to the
  // calibration agent, never given a revenue formula here.
  {
    token: 'radiology',
    label: 'Radiology / imaging',
    parents: ['Diagnostics & labs', 'Hospitals & clinics', 'Specialist medical'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'ophthalmology_general',
    label: 'Ophthalmology (general)',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'neurology',
    label: 'Neurology',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'cardiology',
    label: 'Cardiology',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'gynaecology_obstetrics',
    label: 'Gynaecology / obstetrics',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'endocrinology_diabetes',
    label: 'Endocrinology / diabetes',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'general_surgery',
    label: 'General surgery',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'ent',
    label: 'ENT / otolaryngology',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'urology',
    label: 'Urology',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'oncology',
    label: 'Oncology',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'allergy_immunology',
    label: 'Allergy / immunology',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'rheumatology',
    label: 'Rheumatology',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'pulmonology',
    label: 'Pulmonology / respiratory',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'nephrology',
    label: 'Nephrology',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'vascular_medicine',
    label: 'Vascular medicine',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'orthopaedics_trauma',
    label: 'Orthopaedics / trauma',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'plastic_surgery',
    label: 'Plastic surgery',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'reconstructive_surgery',
    label: 'Reconstructive surgery',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'paediatrics_general',
    label: 'Paediatrics (general)',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'sleep_medicine',
    label: 'Sleep medicine',
    parents: ['Specialist medical', 'Hospitals & clinics'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'mens_health',
    label: 'Men\u2019s health',
    parents: ['Specialist medical'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'hair_transplant',
    label: 'Hair transplant / restoration',
    parents: ['Specialist medical'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'podiatry_chiropody',
    label: 'Podiatry / chiropody',
    parents: ['Specialist medical', 'Physiotherapy & allied health'],
    priority: 'later',
    detect: ['web'],
  },
  {
    token: 'psychology_psychotherapy',
    label: 'Psychology / psychotherapy',
    parents: ['Physiotherapy & allied health', 'Residential specialist care', 'Specialist medical'],
    priority: 'later',
    detect: ['web'],
  },
] as const satisfies ReadonlyArray<ClinicalSpecialism>;

export type ClinicalSpecialismToken = (typeof CLINICAL_SPECIALISMS)[number]['token'];

/** Phase-1 launch-priority subset — the specialisms the ingest classifies first. */
export const LAUNCH_SPECIALISMS = CLINICAL_SPECIALISMS.filter((s) => s.priority === 'launch');

/** token → display label, for surfaces that store the token but show the label
 *  (Screener filter + column). */
export const CLINICAL_SPECIALISM_LABEL: Readonly<Record<string, string>> = Object.fromEntries(
  CLINICAL_SPECIALISMS.map((s) => [s.token, s.label]),
);

/** Canonical subsegments that have at least one relevant clinical specialism — the
 *  cohort where the web-evidence pass has specialisms to find. Subsegments outside
 *  this set (GP, pharma wholesale, medical equipment, hospitals…) are skipped by the
 *  specialism backfill (see services/web-evidence `--relevant-only`). */
export const SPECIALISM_RELEVANT_SUBSEGMENTS: ReadonlySet<string> = new Set(
  CLINICAL_SPECIALISMS.flatMap((s) => s.parents as string[]),
);

/** True when a canonical subsegment has any relevant clinical specialism. */
export function subsegmentHasSpecialisms(subsegment: string | null | undefined): boolean {
  return subsegment != null && SPECIALISM_RELEVANT_SUBSEGMENTS.has(subsegment);
}
